import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import {
  AppstoreOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import RichTextEditor from "components/common/RichTextEditor";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import paths from "routes/paths";
import {
  deleteWorkspace,
  fetchMemberWorkspaces,
  updateWorkspace,
} from "services/workspaceService";
import olreadiaService from "services/olreadiaService";
import identitySubscriptionService from "services/subscriptions";
import WorkspaceInvitations from "./WorkspaceInvitations";
import {
  organizationName,
  formatDateTime,
  PAGE_SIZE,
  readable,
  statusColor,
  subscriptionLabel,
} from "./workspaceUtils";
import { OLIVEEB_COLORS, workspaceButtonStyles, workspaceStyles } from "./colors";

const { Text } = Typography;

const isPersonalAccount = (account) =>
  String(account?.accountType || "").toUpperCase() === "PERSONAL";

const oliveebButtonStyles = {
  view: {
    color: OLIVEEB_COLORS.oliveebBlue,
    background: OLIVEEB_COLORS.oliveebBlueSoft,
    borderColor: OLIVEEB_COLORS.oliveebBlueSoft,
  },
};

const SelectedWorkspaceSummary = () => {
  const navigate = useNavigate();
  const { selectedWorkspace } = useWorkspace();
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [selectedSubscription, setSelectedSubscription] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadSelectedOrganization = async () => {
      if (isPersonalAccount(selectedWorkspace) || !selectedWorkspace?.organizationId) {
        setSelectedOrganization(null);
        return;
      }

      try {
        const entity = await olreadiaService.organizations.get(
          selectedWorkspace.organizationId,
        );
        if (!cancelled) {
          setSelectedOrganization(entity || {});
        }
      } catch (error) {
        if (!cancelled) {
          setSelectedOrganization(null);
        }
      }
    };

    loadSelectedOrganization();

    return () => {
      cancelled = true;
    };
  }, [selectedWorkspace?.organizationId]);

  useEffect(() => {
    let cancelled = false;

    const loadSelectedSubscription = async () => {
      if (!selectedWorkspace?.subscriptionId) {
        setSelectedSubscription(null);
        return;
      }

      try {
        const subscription = await identitySubscriptionService.subscriptions.get(
          selectedWorkspace.subscriptionId,
        );
        if (!cancelled) {
          setSelectedSubscription(subscription || {});
        }
      } catch (error) {
        if (!cancelled) {
          setSelectedSubscription(null);
        }
      }
    };

    loadSelectedSubscription();

    return () => {
      cancelled = true;
    };
  }, [selectedWorkspace?.subscriptionId]);

  return (
    <Card
      size="small"
      style={{
        borderRadius: 8,
        borderColor: selectedWorkspace
          ? OLIVEEB_COLORS.oliveebBlueSoft
          : OLIVEEB_COLORS.goldBorder,
        background: selectedWorkspace
          ? OLIVEEB_COLORS.bgSoft
          : OLIVEEB_COLORS.goldSoft,
        marginBottom: 16,
      }}
    >
      <div className="d-flex align-items-start gap-2 flex-wrap">
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            display: "grid",
            placeItems: "center",
            background: workspaceStyles.iconBubble.background,
            color: workspaceStyles.iconBubble.color,
            flex: "0 0 auto",
          }}
        >
          <AppstoreOutlined />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <Text
            style={{
              display: "block",
              fontSize: 12,
              color: OLIVEEB_COLORS.oliveebGreen,
            }}
          >
            Current workspace
          </Text>
          <Space size={6} align="center" wrap>
            <Text strong style={{ wordBreak: "break-word" }}>
              {selectedWorkspace?.name || "No workspace selected"}
            </Text>
            {selectedWorkspace?.id ? (
              <Button
                size="small"
                aria-label="View current workspace"
                title="View current workspace"
                icon={<EyeOutlined />}
                style={oliveebButtonStyles.view}
                onClick={() => navigate(paths.workspaceDetails(selectedWorkspace.id))}
              />
            ) : null}
          </Space>
          {selectedWorkspace ? (
            <Space wrap size={[4, 4]} style={{ marginTop: 6 }}>
              <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                {readable(selectedWorkspace.product, "Product")}
              </Tag>
              <Tag color="green" style={{ marginInlineEnd: 0 }}>
                {readable(selectedWorkspace.accountType, "Workspace type")}
              </Tag>
              {selectedWorkspace.subscriptionId ? (
                <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                  {subscriptionLabel(selectedSubscription, "Subscription")}
                </Tag>
              ) : null}
              {!isPersonalAccount(selectedWorkspace) && selectedWorkspace.organizationId ? (
                <Space size={4}>
                  <Tag color="cyan" style={{ marginInlineEnd: 0 }}>
                    {organizationName(selectedOrganization, "Organization")}
                  </Tag>
                  <Button
                    size="small"
                    aria-label="View current workspace organization"
                    title="View organization"
                    icon={<EyeOutlined />}
                    style={oliveebButtonStyles.view}
                    onClick={() => navigate(paths.workspaceOrganization(selectedWorkspace.id))}
                  />
                </Space>
              ) : null}
            </Space>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Use the top-right workspace picker to choose one.
            </Text>
          )}
        </div>
      </div>
    </Card>
  );
};

const WorkspacesTable = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshWorkspaces } = useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const [accounts, setAccounts] = useState([]);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [editingAccount, setEditingAccount] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [organizationsById, setOrganizationsById] = useState({});
  const [subscriptionsById, setSubscriptionsById] = useState({});
  const [editForm] = Form.useForm();

  const loadAccounts = useCallback(async (nextPage = 1) => {
    if (!actorId) return;

    try {
      setLoading(true);
      const result = await fetchMemberWorkspaces({
        actorId,
        page: nextPage - 1,
        size: PAGE_SIZE,
      });
      setAccounts(result.content);
      setPage(nextPage);
      setTotal(result.totalElements ?? result.content.length);

      const organizationIds = Array.from(
        new Set(
          result.content
            .filter((account) => !isPersonalAccount(account))
            .map((account) => account.organizationId)
            .filter(Boolean),
        ),
      );
      if (organizationIds.length) {
        const settledEntities = await Promise.allSettled(
          organizationIds.map((id) => olreadiaService.organizations.get(id)),
        );

        setOrganizationsById((current) => {
          const next = { ...current };
          settledEntities.forEach((settled, index) => {
            const id = organizationIds[index];
            next[id] = settled.status === "fulfilled" ? settled.value || {} : null;
          });
          return next;
        });
      }

      const subscriptionIds = Array.from(
        new Set(result.content.map((account) => account.subscriptionId).filter(Boolean)),
      );
      if (subscriptionIds.length) {
        const settledSubscriptions = await Promise.allSettled(
          subscriptionIds.map((id) => identitySubscriptionService.subscriptions.get(id)),
        );

        setSubscriptionsById((current) => {
          const next = { ...current };
          settledSubscriptions.forEach((settled, index) => {
            const id = subscriptionIds[index];
            next[id] = settled.status === "fulfilled" ? settled.value || {} : null;
          });
          return next;
        });
      }
    } catch (error) {
      message.error(error?.message || "Failed to load workspaces.");
    } finally {
      setLoading(false);
    }
  }, [actorId]);

  const deleteWorkspace = async (account) => {
    if (!account?.id) return;

    try {
      setDeletingId(account.id);
      await deleteWorkspace({ actorId, workspaceId: account.id });
      message.success("Workspace deleted.");
      await refreshWorkspaces();
      await loadAccounts(page);
    } catch (error) {
      message.error(error?.message || "Failed to delete workspace.");
    } finally {
      setDeletingId("");
    }
  };

  const openEditWorkspace = (account) => {
    setEditingAccount(account);
    editForm.setFieldsValue({
      name: account?.name || "",
      description: account?.description || "",
    });
  };

  const closeEditWorkspace = () => {
    setEditingAccount(null);
    editForm.resetFields();
  };

  const submitWorkspaceUpdate = async (values) => {
    if (!editingAccount?.id) return;

    const name = String(values.name || "").trim();
    const description = String(values.description || "").trim();
    if (!name) {
      message.error("Enter a workspace name.");
      return;
    }

    try {
      setUpdating(true);
      const updatedAccount = await updateWorkspace({
        actorId,
        workspaceId: editingAccount.id,
        name,
        description,
      });
      setAccounts((current) =>
        current.map((account) =>
          account.id === editingAccount.id
            ? { ...account, ...(updatedAccount || {}), name, description }
            : account,
        ),
      );
      await refreshWorkspaces();
      message.success("Workspace updated.");
      closeEditWorkspace();
    } catch (error) {
      message.error(error?.message || "Failed to update workspace.");
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    loadAccounts(1);
  }, [loadAccounts]);

  const filteredAccounts = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return accounts;

    return accounts.filter((account) => {
      const values = [
        account.name,
        account.description,
        account.product,
        account.accountType,
        account.organizationId,
        organizationName(
          organizationsById[account.organizationId],
          account.organizationId,
        ),
        account.subscriptionId,
        subscriptionLabel(subscriptionsById[account.subscriptionId], account.subscriptionId),
      ];
      return values.some((value) => String(value || "").toLowerCase().includes(term));
    });
  }, [accounts, organizationsById, query, subscriptionsById]);

  const columns = [
    {
      title: "Workspace",
      dataIndex: "name",
      key: "name",
      render: (_, account) => (
        <Space size={10}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              display: "grid",
              placeItems: "center",
              background: workspaceStyles.iconBubble.background,
              color: workspaceStyles.iconBubble.color,
            }}
          >
            <AppstoreOutlined />
          </div>
          <div style={{ minWidth: 0 }}>
            <Text strong>{account.name || "Workspace"}</Text>
            {account.description ? (
              <Text type="secondary" ellipsis style={{ display: "block", maxWidth: 380, fontSize: 12 }}>
                {account.description}
              </Text>
            ) : null}
          </div>
        </Space>
      ),
    },
    {
      title: "Product",
      dataIndex: "product",
      key: "product",
      render: (value, account) => (
        <div>
          <Text>{readable(value, "Product")}</Text>
          <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
            {readable(account.accountType, "Workspace type")}
          </Text>
        </div>
      ),
    },
    {
      title: "Members",
      dataIndex: "users",
      key: "users",
      width: 120,
      render: (users = []) => {
        const activeMembers = users.filter((member) => {
          const status = String(member.membershipStatus || "").toUpperCase();
          return status === "ACTIVE" || status === "ACCEPTED" || status === "";
        });

        return (
          <Space size={6}>
            <TeamOutlined style={{ color: OLIVEEB_COLORS.oliveebGreen }} />
            <Text>{activeMembers.length}</Text>
          </Space>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 180,
      render: (_, account) => {
        const statuses = Array.from(
          new Set((account.users || []).map((member) => member.membershipStatus).filter(Boolean)),
        ).slice(0, 3);

        return statuses.length ? (
          <Space wrap size={[4, 4]}>
            {statuses.map((status) => (
              <Tag key={status} color={statusColor(status)} style={{ marginInlineEnd: 0 }}>
                {readable(status)}
              </Tag>
            ))}
          </Space>
        ) : (
          <Text type="secondary">Not set</Text>
        );
      },
    },
    {
      title: "Links",
      key: "links",
      width: 240,
      render: (_, account) => {
        const showOrganization = !isPersonalAccount(account) && account.organizationId;
        const entity = organizationsById[account.organizationId];
        const entityLabel = showOrganization
          ? organizationName(entity, "Organization")
          : "";
        const subscription = subscriptionsById[account.subscriptionId];
        const subscriptionText = account.subscriptionId
          ? subscriptionLabel(subscription, "Subscription")
          : "";

        return (
          <Space wrap size={[4, 4]}>
            {account.subscriptionId ? (
              <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                {subscriptionText}
              </Tag>
            ) : null}
            {showOrganization ? (
              <Space size={4}>
                <Tag color="cyan" style={{ marginInlineEnd: 0 }}>
                  {entityLabel}
                </Tag>
                <Button
                  size="small"
                  title="View organization"
                  icon={<EyeOutlined />}
                  style={oliveebButtonStyles.view}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (account.id) navigate(paths.workspaceOrganization(account.id));
                  }}
                />
              </Space>
            ) : null}
            {!account.subscriptionId && !showOrganization ? (
              <Text type="secondary">None</Text>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 190,
      render: (value) => formatDateTime(value) || "-",
    },
    {
      title: "",
      key: "actions",
      width: 120,
      align: "right",
      render: (_, account) => (
        <Space size={4}>
          <Button
            title="View"
            icon={<EyeOutlined />}
            style={oliveebButtonStyles.view}
            onClick={(event) => {
              event.stopPropagation();
              if (account.id) navigate(paths.workspaceDetails(account.id));
            }}
          />
          <Button
            title="Edit"
            icon={<EditOutlined />}
            style={{
              borderColor: OLIVEEB_COLORS.oliveebBlue,
              color: OLIVEEB_COLORS.oliveebBlue,
            }}
            onClick={(event) => {
              event.stopPropagation();
              openEditWorkspace(account);
            }}
          />
          <Popconfirm
            title="Delete workspace?"
            description="This action cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true, loading: deletingId === account.id }}
            onConfirm={(event) => {
              event?.stopPropagation?.();
              return deleteWorkspace(account);
            }}
          >
            <Button
              danger
              title="Delete"
              icon={<DeleteOutlined />}
              loading={deletingId === account.id}
              onClick={(event) => event.stopPropagation()}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
        <Space wrap>
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: OLIVEEB_COLORS.placeholder }} />}
            placeholder="Search workspaces"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            style={{ width: 280, borderRadius: 8 }}
          />
        </Space>
        <Space wrap>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate(paths.workspaceCreate())}
            style={{
              borderColor: OLIVEEB_COLORS.oliveebGreen,
              background: OLIVEEB_COLORS.oliveebGreen,
              fontWeight: 700,
            }}
          >
            Add Workspace
          </Button>
          <Button
            icon={<ReloadOutlined />}
            style={workspaceButtonStyles.refresh}
            onClick={() => loadAccounts(page)}
          >
            Refresh
          </Button>
        </Space>
      </div>

      <Card
        styles={{ body: { padding: 0 } }}
        style={{
          borderRadius: 8,
          overflow: "hidden",
          borderColor: OLIVEEB_COLORS.border,
          background: OLIVEEB_COLORS.card,
        }}
      >
        <Table
          rowKey={(record) => record.id || record.name}
          loading={loading}
          columns={columns}
          dataSource={filteredAccounts}
          size="small"
          tableLayout="fixed"
          onRow={(record) => ({
            onClick: () => {
              if (record.id) navigate(paths.workspaceDetails(record.id));
            },
            style: { cursor: record.id ? "pointer" : "default" },
          })}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: query ? filteredAccounts.length : total,
            showSizeChanger: false,
            onChange: (nextPage) => loadAccounts(nextPage),
          }}
          scroll={{ x: 1080 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No workspaces found"
                style={{ padding: "40px 0" }}
              />
            ),
          }}
        />
      </Card>
      <Modal
        title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Edit Workspace</Text>}
        open={Boolean(editingAccount)}
        onCancel={closeEditWorkspace}
        onOk={() => editForm.submit()}
        okText="Save"
        confirmLoading={updating}
        destroyOnHidden
      >
        <Form
          form={editForm}
          layout="vertical"
          requiredMark={false}
          onFinish={submitWorkspaceUpdate}
        >
          <Form.Item
            label="Workspace name"
            name="name"
            rules={[{ required: true, message: "Enter workspace name." }]}
          >
            <Input placeholder="Workspace name" autoFocus />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <RichTextEditor height="180px" placeholder="Optional workspace description" />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

const WorkspacesPage = () => (
  <div className="project-page">
    <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
      <WidgetSectionTitle
        icon={<AppstoreOutlined />}
        title="Workspaces"
        subtitle="Review product access, memberships, subscriptions, and organization links tied to your workspaces"
        className="flex-1"
        titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
        subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
      />
    </div>

    <SelectedWorkspaceSummary />

    <Tabs
      className="pa-top-tabs"
      defaultActiveKey="accounts"
      items={[
        {
          key: "accounts",
          label: "Workspaces",
          children: <WorkspacesTable />,
        },
        {
          key: "invitations",
          label: "Invitations",
          children: <WorkspaceInvitations />,
        },
      ]}
    />
  </div>
);

export default WorkspacesPage;
